import React from "react";
import styled, { css } from "styled-components";
import { TextBody } from "./index";
import { ThemeColor } from "../styles/theme";
import {
  animated,
  useChain,
  useSpring,
  useSpringRef,
  useTransition,
} from "react-spring";
import { useInView } from "react-intersection-observer";
import Title from "./Title";
import { device } from "../styles/media-query";
import { useMyMediaQuery } from "../hooks/useMediaQuery";

import MrMute from "../assets/icon/MrMute.png";
import Badcop from "../assets/icon/badcop.png";
import Roa from "../assets/icon/roa.png";
import Scal from "../assets/icon/sca.png";
import Jooker from "../assets/icon/jooker.png";
import ProfileMore from "../assets/icon/ProfileMore.svg";

const Container = styled.section`
  box-sizing: border-box;
  overflow: hidden;
  width: 100%;
  position: relative;

  ${device.tablet} {
    padding: 12rem 6rem;
  }

  ${device.mobile} {
    padding: 8rem 2rem;
  }

  ${device.desktop} {
    max-width: 1080px;
    margin: 0 auto;
    padding: 20rem 4rem;
  }
`;

const TeamList = styled.ul`
  width: 100%;
  padding: 0;

  flex-wrap: wrap;
  display: flex;
  @media (min-width: 1280px) {
    //margin-bottom: -8rem;
  }
  @media (min-width: 600px) and (max-width: 1280px) {
    //margin-bottom: -8rem;
  }

  @media (max-width: 600px) {
    //margin-right: -7.5px;
    //margin-left: -7.5px;
    //margin-bottom: -6rem;
  }

  @media (max-width: 374px) {
    margin-right: 0;
    margin-left: 0;
  }
`;

const TeamItem = styled.li<{ hasLink: boolean; cursor: string }>`
  cursor: ${({ cursor }) => cursor};

  &:hover {
    transform: translateY(-3%);
    ${({ hasLink }) =>
      hasLink &&
      css`
        & div div {
          opacity: 1;
        }
      `};
  }

  @media (min-width: 1080px) {
    margin-right: 140px;
    margin-bottom: 80px;
    width: 240px;
    &:nth-child(3n) {
      margin-right: 0;
    }
  }

  @media (min-width: 600px) and (max-width: 1079px) {
    margin-right: 60px;
    margin-bottom: 80px;
    width: calc(50% - 30px);
    &:nth-child(2n) {
      margin-right: 0;
    }
  }

  @media (max-width: 600px) {
    margin-right: 15px;
    margin-bottom: 6rem;
    width: calc(50% - 7.5px);
    &:nth-child(2n) {
      margin-right: 0;
    }
  }

  @media (max-width: 374px) {
    margin-bottom: 6rem;
    padding: 0;
    width: 100%;
  }

  transition: transform 0.4s ease-in-out;
`;

const ProfileImageContainer = styled.div<{ hasLink: boolean }>`
  position: relative;

  ${({ hasLink }) =>
    hasLink &&
    css`
      &:hover {
        & > div {
          opacity: 1;
        }
      }
    `};
`;

const ProfileOverlay = styled.div`
  background: rgba(0, 0, 0, 0.7);
  z-index: 5;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  position: absolute;
  opacity: 0;
  transition: all 0.5s ease-in-out;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;
const ProfileImage = styled.img`
  width: 100%;
  aspect-ratio: 1 / 1;
  height: 100%;
  @media (max-width: 375px) {
    //width: 160px;
    width: 100%;
    height: 160px;
  }
`;

const ITEMS = [
  {
    name: "Mr.Mute",
    role: "Artist",
    career:
      "Serial enterperneneur with multiple exits \n" +
      "Crypto & Blockchain maximalist \n" +
      "Ph.D at Interaction Design",
    image: MrMute,
    link: "https://www.naver.com",
  },
  {
    name: "Amazon Tiger",
    role: "Tech Master",
    career: "Amazon SDE\n Samsung SDE\n Startup Co-Founder, CTO",
    image: MrMute,
    link: "",
  },
  {
    name: "Scalene",
    role: "Crypto-evangelist",
    career:
      "Serial entrepreneur \n" +
      "Startup angel investor\n" +
      "Crypto & Blockchain maximalist\n" +
      "MBA at KAIST",
    image: Scal,
    link: "",
  },
  {
    name: "Jooker",
    role: "Design Master",
    career:
      "tvN Broadcast visual art designer\n" +
      "Naver Motion & interaction graphics designer",
    image: Jooker,
    link: "",
  },
  {
    name: "Bad cop",
    role: "Almighty",
    career: "Serial enterpreneur with exit\n VC Partner\n Angel investor",
    image: Badcop,
    link: "",
  },
  {
    name: "ROARIPARK",
    role: "Strategy $$$",
    career:
      "Serial enterperneneur with exit\n" +
      "Fintech / Crypto & Blockchain\n" +
      "Startup Co-Founder, CTO",
    image: Roa,
    link: "",
  },
];

function Team() {
  const { isMobile, isTablet } = useMyMediaQuery();
  const { ref, inView } = useInView({
    rootMargin: "-60px 0px 0px 0px",
  });

  const transitionRef = useSpringRef();
  const transitions = useTransition(ITEMS, {
    ref: transitionRef,
    from: { opacity: 0, transform: "translateY(30px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    trail: 500,
  });

  const goToLink = (link: string) => {
    window.open(link, "_blank");
  };

  useChain(inView ? [transitionRef] : []);

  const { ref: titleRef, inView: titleInView } = useInView({
    rootMargin: "-120px 0px 0px 0px",
    triggerOnce: true,
  });

  const titleStyle = useSpring({
    to: {
      transform: `translateY(${titleInView ? "0" : "30"}px)`,
      opacity: titleInView ? 1 : 0,
    },
    config: { duration: isMobile ? 500 : 350 },
  });

  return (
    <Container id={"team"}>
      <animated.div style={titleStyle}>
        <div ref={titleRef}>
          <Title text={"Team"} />
        </div>
      </animated.div>
      <div
        style={{ marginTop: isTablet ? 60 : isMobile ? 60 : 80, width: "100%" }}
      >
        <TeamList ref={ref}>
          {transitions((style, item) => (
            <TeamItem
              hasLink={!!item.link}
              onClick={() => {
                if (item.link) goToLink(item.link);
              }}
              cursor={item.link != null ? "pointer" : "unset"}
            >
              <div>
                <animated.div style={style}>
                  <ProfileImageContainer hasLink={!!item.link}>
                    <ProfileOverlay>
                      <img
                        src={ProfileMore}
                        width={80}
                        height={80}
                        alt={"profile_more"}
                      />
                    </ProfileOverlay>
                    <ProfileImage
                      src={item.image}
                      alt={"ProfileImage"}
                      // width={isMobile ? 160 : isTablet ? 200 : 240}
                      height={isMobile ? 160 : isTablet ? 200 : 240}
                    />
                  </ProfileImageContainer>
                  <TextBody
                    bold
                    color={ThemeColor.WHITE}
                    style={{
                      marginTop: "2rem",
                      fontSize: isMobile ? "2.4rem" : "2.6rem",
                    }}
                  >
                    {item.name}
                  </TextBody>
                  <TextBody
                    bold
                    style={{
                      color: "#EA3D61",
                      marginTop: "0.6rem",
                      marginBottom: "1.2rem",
                    }}
                  >
                    {item.role}
                  </TextBody>
                  {item.career.split("\n").map((r) => (
                    <TextBody
                      size={isMobile ? 14 : 16}
                      color={ThemeColor.WHITE}
                    >
                      {r}
                    </TextBody>
                  ))}
                </animated.div>
              </div>
            </TeamItem>
          ))}
        </TeamList>
      </div>
    </Container>
  );
}

export default Team;
